import React from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { motion } from "framer-motion";
import { Download, Chrome, Smartphone, Apple, ArrowRight, Keyboard, Globe, Zap, Shield, Bot, Sparkles, MessageSquare, Languages, Target, Monitor } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function DownloadsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const extensionFeatures = [
    { icon: Zap, title: "Right-Click Rewrite", desc: "Select text on any website, right-click, and rewrite with Buddy instantly." },
    { icon: Globe, title: "Works Everywhere", desc: "Gmail, LinkedIn, Twitter, WhatsApp Web, and any text field on the web." },
    { icon: Bot, title: "Floating Panel", desc: "Sleek panel appears right next to your selected text with all Buddy features." },
    { icon: Target, title: "Intent Detect", desc: "AI auto-detects your sentence tone and suggests 3 best rewrites." },
  ];

  const mobileFeatures = [
    { icon: Keyboard, title: "System-Wide Keyboard", desc: "Two-button system: Buddy icon shows tones, AI icon shows 3 intent-detected rewrites." },
    { icon: Sparkles, title: "All 11 Tones", desc: "Tap Buddy icon to pick any of 11 emotional tones. Tap AI icon for auto-detected suggestions." },
    { icon: MessageSquare, title: "Opens to AI Tools", desc: "The Buddy mobile app launches directly into AI Tools — Summarizer, CaptionCraft, Resume Checker, Cover Letter Builder, and more." },
    { icon: Languages, title: "Translate & Reply", desc: "Translate to 20+ languages, generate smart replies, all from your keyboard." },
  ];

  const extensionSteps = ["Download the Buddy extension zip file below", "Open chrome://extensions in your Chrome browser", "Enable 'Developer mode' in the top-right corner", "Click 'Load unpacked' and select the extracted Buddy folder", "Pin the Buddy icon to your toolbar"];

  const handleExtDownload = () => {
    // Open to all users — no sign-in required. Plan-based feature access is
    // enforced inside the extension at runtime, not at download time.
    fetch(`${BACKEND_URL}/api/download/extension`)
      .then(r => { if (!r.ok) throw new Error(); return r.blob(); })
      .then(blob => { const url = URL.createObjectURL(blob); const a = document.createElement("a"); a.href = url; a.download = "buddy-extension.zip"; a.click(); URL.revokeObjectURL(url); })
      .catch(() => alert("Download failed. Please check your connection and try again."));
  };

  return (
    <div className="min-h-screen" style={{ background: "var(--surface-cream)" }}>
      <nav className="sticky top-0 z-50" style={{ background: "rgba(250,250,248,0.8)", backdropFilter: "blur(16px)", borderBottom: "1px solid rgba(232,228,220,0.6)" }}>
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
          <h1 className="font-display text-3xl font-bold cursor-pointer" style={{ color: "var(--rolex-green)" }} onClick={() => navigate("/")} data-testid="downloads-logo">Buddy</h1>
          <div className="flex items-center gap-3">
            {user ? <button onClick={() => navigate("/app")} className="btn-primary py-2 px-5 text-sm">Dashboard</button>
              : <button onClick={() => navigate("/login")} className="btn-primary py-2 px-5 text-sm">Sign In</button>}
          </div>
        </div>
      </nav>

      <section className="max-w-5xl mx-auto px-4 pt-16 pb-12 text-center">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <h1 className="font-display text-3xl md:text-4xl font-bold" style={{ color: "var(--text-primary)" }}>Get Buddy Everywhere</h1>
          <p className="mt-3 text-base" style={{ color: "var(--text-muted)" }}>Same Buddy on every screen.</p>
        </motion.div>
      </section>

      {/* Browser Extension */}
      <section className="max-w-5xl mx-auto px-4 pb-14">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
          <div className="rounded-2xl overflow-hidden" style={{ background: "rgba(255,255,255,0.65)", backdropFilter: "blur(12px)", border: "1px solid rgba(232,228,220,0.5)" }}>
            <div className="p-8 pb-0 flex items-center gap-3 mb-2">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: "var(--rolex-green-light)" }}><Chrome size={24} style={{ color: "var(--rolex-green)" }} /></div>
              <div><h2 className="font-display text-xl font-semibold" style={{ color: "var(--text-primary)" }}>Browser Extension</h2><p className="text-sm" style={{ color: "var(--text-muted)" }}>Chrome, Edge, Brave & all Chromium browsers</p></div>
            </div>
            <div className="grid md:grid-cols-2 gap-8 p-8">
              <div>
                <span className="label-caps block mb-4" style={{ color: "var(--rolex-gold)" }}>Features</span>
                <div className="space-y-3">
                  {extensionFeatures.map((f, i) => (
                    <div key={i} className="flex items-start gap-3"><div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 mt-0.5" style={{ background: "rgba(0,96,57,0.08)" }}><f.icon size={15} style={{ color: "var(--rolex-green)" }} /></div>
                    <div><p className="text-sm font-medium" style={{ color: "var(--text-primary)" }}>{f.title}</p><p className="text-xs leading-relaxed" style={{ color: "var(--text-muted)" }}>{f.desc}</p></div></div>
                  ))}
                </div>
              </div>
              <div>
                <span className="label-caps block mb-4" style={{ color: "var(--rolex-gold)" }}>Install in 5 Steps</span>
                <div className="space-y-2.5">{extensionSteps.map((step, i) => <div key={i} className="flex items-start gap-3"><span className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 mt-0.5" style={{ background: "var(--rolex-green)", color: "white" }}>{i + 1}</span><p className="text-sm leading-relaxed" style={{ color: "var(--text-secondary)" }}>{step}</p></div>)}</div>
                <motion.button onClick={handleExtDownload} className="btn-primary py-3 px-6 text-sm mt-6 inline-flex items-center gap-2" whileHover={{ scale: 1.02 }} data-testid="download-extension-btn"><Download size={16} /> Download Extension (.zip)</motion.button>
              </div>
            </div>
          </div>
        </motion.div>
      </section>

      {/* Mobile Keyboard App */}
      <section className="max-w-5xl mx-auto px-4 pb-14">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
          <div className="rounded-2xl overflow-hidden" style={{ background: "rgba(255,255,255,0.65)", backdropFilter: "blur(12px)", border: "1px solid rgba(232,228,220,0.5)" }}>
            <div className="p-8 pb-0 flex items-center gap-3 mb-2">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: "var(--rolex-gold-light)" }}><Smartphone size={24} style={{ color: "var(--rolex-gold)" }} /></div>
              <div><h2 className="font-display text-xl font-semibold" style={{ color: "var(--text-primary)" }}>Buddy Mobile App & Keyboard</h2><p className="text-sm" style={{ color: "var(--text-muted)" }}>iOS & Android — Keyboard + Full AI Tools</p></div>
            </div>
            <div className="grid md:grid-cols-2 gap-8 p-8">
              <div>
                <span className="label-caps block mb-4" style={{ color: "var(--rolex-gold)" }}>Keyboard & App Features</span>
                <div className="space-y-3">{mobileFeatures.map((f, i) => (
                  <div key={i} className="flex items-start gap-3"><div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 mt-0.5" style={{ background: "rgba(166,124,82,0.1)" }}><f.icon size={15} style={{ color: "var(--rolex-gold)" }} /></div>
                  <div><p className="text-sm font-medium" style={{ color: "var(--text-primary)" }}>{f.title}</p><p className="text-xs leading-relaxed" style={{ color: "var(--text-muted)" }}>{f.desc}</p></div></div>
                ))}</div>
              </div>
              <div>
                <span className="label-caps block mb-4" style={{ color: "var(--rolex-gold)" }}>How to Enable Buddy Keyboard</span>
                <div className="p-4 rounded-xl mb-4" style={{ background: "var(--surface-warm)" }}>
                  <p className="text-sm font-semibold mb-3" style={{ color: "var(--text-primary)" }}>Turn Buddy into your default keyboard</p>
                  <div className="space-y-3 text-xs leading-relaxed" style={{ color: "var(--text-muted)" }}>
                    <div>
                      <p className="font-semibold mb-1" style={{ color: "var(--rolex-green)" }}>iOS (iPhone / iPad)</p>
                      <ol className="list-decimal ml-4 space-y-1">
                        <li>Install Buddy from the App Store and open it once so it registers with the system.</li>
                        <li>Open <strong>Settings → General → Keyboard → Keyboards → Add New Keyboard…</strong></li>
                        <li>Pick <strong>Buddy</strong> from the list, then tap it again and toggle on <strong>Allow Full Access</strong> (required for AI rewrites).</li>
                        <li>In any app (WhatsApp, Mail, Instagram, LinkedIn) long-press the globe 🌐 key on your keyboard and switch to <strong>Buddy</strong>.</li>
                      </ol>
                    </div>
                    <div>
                      <p className="font-semibold mb-1" style={{ color: "var(--rolex-green)" }}>Android</p>
                      <ol className="list-decimal ml-4 space-y-1">
                        <li>Install Buddy from Google Play and open the app once.</li>
                        <li>Go to <strong>Settings → System → Languages &amp; input → On-screen keyboard → Manage keyboards</strong> and toggle <strong>Buddy</strong> on.</li>
                        <li>Back in any app, tap the keyboard-switch icon in the nav bar (or long-press the space bar) and select <strong>Buddy</strong> as your active keyboard.</li>
                      </ol>
                    </div>
                    <p className="pt-2" style={{ color: "var(--text-light)" }}>
                      Once enabled, Buddy replaces your usual keyboard everywhere — WhatsApp, Instagram, Gmail, LinkedIn, Slack, Notes — with the same 11 tones and AI Intent Detect you already use on the web.
                    </p>
                  </div>
                </div>
                <div className="mt-5">
                  <div className="flex gap-3">
                    <motion.button className="btn-primary py-2.5 px-5 text-sm flex items-center gap-2" whileHover={{ scale: 1.02 }} data-testid="mobile-appstore-btn"><Apple size={15} /> App Store</motion.button>
                    <motion.button className="btn-secondary py-2.5 px-5 text-sm flex items-center gap-2" whileHover={{ scale: 1.02 }} data-testid="mobile-playstore-btn"><Smartphone size={15} /> Play Store</motion.button>
                  </div>
                  <p className="text-xs mt-3 flex items-start gap-1.5" style={{ color: "var(--text-light)" }}>
                    <span className="w-1.5 h-1.5 rounded-full shrink-0 mt-1.5" style={{ background: "var(--rolex-green)" }} />
                    Sign in once on the web — your session syncs automatically to the extension, mobile app, and keyboard.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </section>

      {/* ─── Windows desktop app ─── */}
      <section className="max-w-5xl mx-auto px-4 pb-14">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}>
          <div className="rounded-2xl overflow-hidden" style={{ background: "rgba(255,255,255,0.65)", backdropFilter: "blur(12px)", border: "1px solid rgba(232,228,220,0.5)" }}>
            <div className="p-8 pb-0 flex items-center gap-3 mb-2">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: "var(--rolex-green-light)" }}>
                <Monitor size={24} style={{ color: "var(--rolex-green)" }} />
              </div>
              <div>
                <h2 className="font-display text-xl font-semibold" style={{ color: "var(--text-primary)" }}>Buddy for Windows</h2>
                <p className="text-sm" style={{ color: "var(--text-muted)" }}>Native desktop app for Windows 10 &amp; 11</p>
              </div>
              <span className="ml-auto text-xs font-semibold px-2.5 py-1 rounded-full" style={{ background: "var(--rolex-green-light)", color: "var(--rolex-green)" }}>NEW</span>
            </div>
            <div className="grid md:grid-cols-2 gap-8 p-8">
              <div>
                <span className="label-caps block mb-4" style={{ color: "var(--rolex-gold)" }}>What you get</span>
                <div className="space-y-3">
                  {[
                    { icon: Keyboard, title: "Global hotkeys", desc: "Ctrl+Shift+Space opens a floating Buddy anywhere. Ctrl+Shift+R gives you Smart Reply." },
                    { icon: Zap, title: "System tray + right-click", desc: "Buddy lives quietly in the tray. Rewrite / Humanize / Translate / Summarize from any Windows app." },
                    { icon: Shield, title: "One account, everywhere", desc: "Same sign-in, tones, library, and plan as the web app. Tokens stay in Chromium's DPAPI-encrypted store." },
                    { icon: Sparkles, title: "Unlimited free grammar check", desc: "Live grammar suggestions on every text field — never counts against your rewrite quota." },
                  ].map((f, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 mt-0.5" style={{ background: "rgba(0,96,57,0.10)" }}>
                        <f.icon size={15} style={{ color: "var(--rolex-green)" }} />
                      </div>
                      <div>
                        <p className="text-sm font-medium" style={{ color: "var(--text-primary)" }}>{f.title}</p>
                        <p className="text-xs leading-relaxed" style={{ color: "var(--text-muted)" }}>{f.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <span className="label-caps block mb-4" style={{ color: "var(--rolex-gold)" }}>Install</span>
                <div className="p-4 rounded-xl" style={{ background: "var(--surface-warm)" }}>
                  <ol className="text-xs space-y-1.5 list-decimal ml-4" style={{ color: "var(--text-secondary)" }}>
                    <li>Download <code>Buddy-Windows.zip</code> (~102 MB)</li>
                    <li>Right-click the zip → <b>Extract All…</b></li>
                    <li>Open the extracted folder and double-click <code>Buddy.exe</code></li>
                    <li>Press <b>Ctrl+Shift+Space</b> anywhere to summon Buddy</li>
                  </ol>
                </div>
                <motion.a
                  href={`${BACKEND_URL}/api/download/windows`}
                  className="btn-primary py-3 px-6 text-sm mt-6 inline-flex items-center gap-2"
                  whileHover={{ scale: 1.02 }}
                  data-testid="download-windows-btn"
                  download="Buddy-Windows.zip"
                >
                  <Download size={16} /> Download for Windows
                </motion.a>
                <p className="text-xs mt-3" style={{ color: "var(--text-light)" }}>
                  macOS & Linux builds available on request.
                </p>
                <div className="mt-4 p-3 rounded-lg flex items-start gap-2" style={{ background: "var(--rolex-gold-light)", border: "1px solid rgba(166,124,82,0.25)" }} data-testid="office-teams-coming-soon">
                  <Sparkles size={14} style={{ color: "var(--rolex-gold)", marginTop: "2px" }} />
                  <p className="text-xs leading-relaxed" style={{ color: "var(--text-secondary)" }}>
                    <b style={{ color: "var(--rolex-gold)" }}>Coming Soon:</b> Native MS Office &amp; Microsoft Teams integration — right-click Buddy inside Word, Outlook, and Teams chats.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </section>


      <section className="max-w-5xl mx-auto px-4 pb-16">
        <div className="rounded-2xl p-6 text-center" style={{ background: "rgba(0,96,57,0.04)", border: "1px solid rgba(0,96,57,0.1)" }}>
          <h3 className="text-sm font-semibold mb-2" style={{ color: "var(--rolex-green)" }}>One Plan, Every Platform</h3>
          <p className="text-xs leading-relaxed max-w-lg mx-auto" style={{ color: "var(--text-muted)" }}>Your Buddy account works across web, browser extension, Windows desktop, and mobile keyboard. Sign in once, use everywhere.</p>
        </div>
      </section>

      <footer className="py-10" style={{ borderTop: "1px solid rgba(232,228,220,0.5)", background: "var(--surface-white)" }}>
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h2 className="font-display text-2xl font-bold cursor-pointer" style={{ color: "var(--rolex-green)" }} onClick={() => navigate("/")}>Buddy</h2>
          <p className="text-xs mt-1" style={{ color: "var(--text-light)" }}>Say it, The way you mean it.</p>
          <button
            onClick={() => window.dispatchEvent(new CustomEvent("buddy:open-feedback"))}
            className="text-xs font-semibold mt-3 transition-opacity hover:opacity-80"
            style={{ color: "var(--rolex-green)", background: "none", border: "none", cursor: "pointer" }}
            data-testid="footer-feedback-btn"
          >
            Feedback
          </button>
        </div>
      </footer>
    </div>
  );
}
